import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import { Warning as WarningIcon, Delete as DeleteIcon } from '@mui/icons-material';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 12px;
    max-width: 500px;
    width: 100%;
  }
`;

const DialogTitleStyled = styled(DialogTitle)`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #d32f2f;
  font-weight: 600;
  padding: 24px 24px 16px;
`;

const DialogContentStyled = styled(DialogContent)`
  padding: 0 24px 16px;
`;

const DialogActionsStyled = styled(DialogActions)`
  padding: 16px 24px 24px;
  gap: 12px;
`;

const WarningBox = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 16px 0;
  padding: 16px;
  background-color: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 8px;
`;

interface ModalDeletePlanProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading: boolean;
  planName?: string;
  canDelete?: boolean;
  userCount?: number;
}

export function ModalDeletePlan({
  open,
  onClose,
  onConfirm,
  loading,
  planName,
  canDelete = true,
  userCount = 0,
}: ModalDeletePlanProps) {
  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error is handled by the parent component
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleStyled>
        <DeleteIcon />
        Confirmar Exclusão do Plano
      </DialogTitleStyled>

      <DialogContentStyled>
        {canDelete ? (
          <>
            <Typography variant="body1" color="text.primary" mb={2}>
              {planName
                ? `Tem certeza que deseja excluir o plano "${planName}"?`
                : 'Tem certeza que deseja excluir este plano?'
              }
            </Typography>

            <WarningBox>
              <WarningIcon color="warning" />
              <Box>
                <Typography variant="body2" color="text.primary" fontWeight={600} mb={1}>
                  Atenção: Esta ação não pode ser desfeita!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Todos os usuários associados a este plano serão afetados
                  <br />
                  • Os dados relacionados ao plano serão permanentemente removidos
                  <br />
                  • Esta operação é irreversível
                </Typography>
              </Box>
            </WarningBox>

            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Importante:</strong> Certifique-se de que não há usuários ativos
                dependendo deste plano antes de prosseguir.
              </Typography>
            </Alert>
          </>
        ) : (
          <>
            <Typography variant="body1" color="text.primary" mb={2}>
              Não é possível excluir o plano "{planName}"
            </Typography>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Motivo:</strong> Este plano possui {userCount} usuário(s) cadastrado(s).
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Para excluir este plano, você deve primeiro:
            </Typography>

            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Remover todos os usuários associados ao plano
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Ou migrar os usuários para outro plano
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Ou desativar o plano em vez de excluí-lo
              </Typography>
            </Box>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Dica:</strong> Use o botão "Visualizar usuários" para ver quais usuários estão associados a este plano.
              </Typography>
            </Alert>
          </>
        )}
      </DialogContentStyled>

      <Divider />

      <DialogActionsStyled>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          size="large"
        >
          {canDelete ? 'Cancelar' : 'Fechar'}
        </Button>
        {canDelete && (
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            size="large"
          >
            {loading ? 'Excluindo...' : 'Confirmar Exclusão'}
          </Button>
        )}
      </DialogActionsStyled>
    </StyledDialog>
  );
}
